import React from 'react';

import Data from 'data';
import Store from 'store';

import { useIconLibrary } from './IconLibrary';
import { socketPositions } from 'graphics';
import { presetItems } from 'logic/itemTypes';
import GameContext from 'gamectx';

function ItemIconLoader({ lib, id, onLoad, index }) {
  Store.useItem(id);
  const image = lib(id);
  React.useEffect(() => {
    image.then(icon => onLoad(index, icon));
  }, [image, index, onLoad]);
  return null;
}

export default React.forwardRef(function ItemImage({ id, sockets, ...props }, ref) {
  const item = Store.useItem(id) || presetItems[id];
  const base = item && Data.items[item.base];
  const [icons, setIcons] = React.useState([]);
  const [gemsocket, setSocket] = React.useState();
  const iconLib = useIconLibrary();
  const canvasRef = React.useRef();
  const { gfx, gridSize } = React.useContext(GameContext);

  React.useEffect(() => gfx.getCommonImage('gemsocket').then(icon => {
    if (canvasRef.current) setSocket(icon);
  }), [gfx]);

  const onLoad = React.useCallback((index, icon) => {
    if (!canvasRef.current) return;
    setIcons(icons => {
      if (icons[index] === icon) return icons;
      icons = icons.slice();
      icons[index] = icon;
      return icons;
    });
  }, []);

  const numSockets = item.sockets, ethereal = item.ethereal || item.stats?.item_ethereal;
  React.useEffect(() => {
    const image = icons[0];
    if (!canvasRef.current || !image || !image.frames.length) return;

    const width = base.invwidth * gridSize;
    const height = base.invheight * gridSize;
    const ctx = canvasRef.current.getContext("2d");

    ctx.clearRect(0, 0, width, height);

    const frame = image.frames[0];
    ctx.globalAlpha = (ethereal ? 0.5 : 1);
    ctx.drawImage(image.bitmap, frame.x, frame.y, frame.width, frame.height, 0, 0, frame.width, frame.height);
    ctx.globalAlpha = 1.0;

    if (!numSockets) return;
    const pos = socketPositions[`${base.invheight}x${base.invwidth}`]?.[numSockets - 1];
    if (!pos) return;

    const HalfSize = gridSize / 2;
    if (sockets) {
      const sfrm = gemsocket?.frames[0];
      if (sfrm) {
        ctx.globalAlpha = 0.25;
        for (let i = 0; i < pos.length; i += 2) {
          ctx.drawImage(gemsocket.bitmap, sfrm.x, sfrm.y, sfrm.width, sfrm.height, Math.round(pos[i] * HalfSize) - 1, Math.round(pos[i + 1] * HalfSize) + 1, sfrm.width, sfrm.height);
        }
        ctx.globalAlpha = 1.0;
      }
      for (let i = 0; i + 1 < icons.length && i < numSockets; ++i) {
        const frm = icons[i + 1]?.frames[0];
        if (!frm) continue;
        ctx.drawImage(icons[i + 1].bitmap, frm.x, frm.y, frm.width, frm.height, Math.round(pos[i * 2] * HalfSize), Math.round(pos[i * 2 + 1] * HalfSize), frm.width, frm.height);
      }
    }
  }, [iconLib, id, base, numSockets, icons, ethereal, gemsocket, sockets, gridSize]);

  return (
    <div {...props} style={{width: base.invwidth * gridSize, height: base.invheight * gridSize}} ref={ref}>
      <canvas width={base.invwidth * gridSize} height={base.invheight * gridSize} ref={canvasRef}/>
      <ItemIconLoader lib={iconLib} id={id} onLoad={onLoad} index={0}/>
      {[...Array(6)].map((_, i) => <ItemIconLoader key={i} lib={iconLib} id={item.socketedItems?.[i]} onLoad={onLoad} index={i + 1}/>)}
    </div>
  );
});
